import { useAuth } from '@/hooks/useAuth';
import { useAuthQuery } from '@/hooks/useAuthQuery';

/**
 * Collection Module Permission Hooks
 * Following Procurement architecture for consistency
 */

// Enhanced permission hook with project-aware caching (same as Procurement)
const useOptimizedPermissionCheck = (permissionCode: string, projectId?: string) => {
  const { user } = useAuth();
  
  return useAuthQuery<boolean>({
    queryKey: ['collection-permissions', user?.id, projectId, permissionCode],
    queryFn: async () => {
      const { checkUserPermission } = await import('@/lib/queries/authQueries');
      return checkUserPermission(user?.id || '', permissionCode);
    },
    requiresAuth: true,
    requiresSession: true,
    enabled: !!permissionCode && !!user?.id,
    staleTime: 60 * 1000,
    gcTime: 30 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });
};

// Base access check
export const useCollectionAccess = (projectId?: string) => {
  return useOptimizedPermissionCheck('COLLECTION_ACCESS', projectId);
};

// View permissions
export const useCollectionViewPermissions = (projectId?: string) => {
  return useOptimizedPermissionCheck('COLLECTION_VIEW', projectId);
};

// Contract permissions
export const useCollectionContractPermissions = (projectId?: string) => {
  const createContract = useOptimizedPermissionCheck('COLLECTION_CREATE_CONTRACT', projectId);
  const editContract = useOptimizedPermissionCheck('COLLECTION_EDIT_CONTRACT', projectId);
  const deleteContract = useOptimizedPermissionCheck('COLLECTION_DELETE_CONTRACT', projectId);

  return {
    createContract: createContract.data ?? false,
    editContract: editContract.data ?? false,
    deleteContract: deleteContract.data ?? false,
    isLoading: createContract.isLoading || editContract.isLoading || deleteContract.isLoading,
  };
};

// Receipt permissions
export const useCollectionReceiptPermissions = (projectId?: string) => {
  const createReceipt = useOptimizedPermissionCheck('COLLECTION_CREATE_RECEIPT', projectId);
  const editReceipt = useOptimizedPermissionCheck('COLLECTION_EDIT_RECEIPT', projectId);
  const deleteReceipt = useOptimizedPermissionCheck('COLLECTION_DELETE_RECEIPT', projectId);
  const toggleStatus = useOptimizedPermissionCheck('COLLECTION_TOGGLE_STATUS', projectId);
  const lockReceipt = useOptimizedPermissionCheck('COLLECTION_LOCK_RECEIPT', projectId);

  return {
    createReceipt: createReceipt.data ?? false,
    editReceipt: editReceipt.data ?? false,
    deleteReceipt: deleteReceipt.data ?? false,
    toggleStatus: toggleStatus.data ?? false,
    lockReceipt: lockReceipt.data ?? false,
    isLoading: createReceipt.isLoading || editReceipt.isLoading || deleteReceipt.isLoading || 
               toggleStatus.isLoading || lockReceipt.isLoading,
  };
};

// Customer permissions
export const useCollectionCustomerPermissions = (projectId?: string) => {
  return useOptimizedPermissionCheck('COLLECTION_MANAGE_CUSTOMERS', projectId);
};

// UI-ready permission hook (composite)
export const useCollectionUIPermissions = (projectId: string) => {
  const contractPerms = useCollectionContractPermissions(projectId);
  const receiptPerms = useCollectionReceiptPermissions(projectId);
  const customerPerms = useCollectionCustomerPermissions(projectId);

  const isLoading = contractPerms.isLoading || receiptPerms.isLoading || customerPerms.isLoading;

  return {
    // Contract permissions
    canCreateContract: contractPerms.createContract,
    canEditContract: contractPerms.editContract,
    canDeleteContract: contractPerms.deleteContract,

    // Receipt permissions
    canCreateReceipt: receiptPerms.createReceipt,
    canEditReceipt: receiptPerms.editReceipt,
    canDeleteReceipt: receiptPerms.deleteReceipt,
    canToggleReceiptStatus: receiptPerms.toggleStatus,
    canLockReceipt: receiptPerms.lockReceipt,

    // Customer permissions
    canManageCustomers: customerPerms.data ?? false,

    // Loading state
    isLoading,

    // UI Helper: Get props for disabled elements
    getDisabledProps: (hasPermission: boolean, additionalDisabled = false) => ({
      disabled: !hasPermission || additionalDisabled || isLoading,
      className: !hasPermission || additionalDisabled ? 'opacity-50 cursor-not-allowed' : ''
    }),

    // UI Helper: Get tooltip text for disabled states
    getTooltipText: (hasPermission: boolean, action: string) => {
      if (isLoading) return 'Đang kiểm tra quyền...';
      if (!hasPermission) return `Bạn không có quyền ${action}`;
      return '';
    }
  };
};
